import { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Spinner,
} from "flowbite-react";
import {
  LuBot,
  LuBuilding2,
  LuExternalLink,
  LuImagePlus,
  LuMap,
  LuMapPin,
  LuSearch,
  LuSend,
  LuShieldCheck,
  LuX,
} from "react-icons/lu";
import { aiApi } from "../api/aiApi";
import { quickSearchApi } from "../api/quickSearchApi";
import QuickSearchAiAnalysis, {
  extractGroundingSources as extractQuickSearchSources,
  extractModelText,
  QuickSearchMarkdown,
  SourceLinks,
} from "../components/QuickSearchAiAnalysis";
import {
  BlueprintPreview,
  measureParcelGeometry,
} from "../components/Steps/shared/PropertyRegridSnapshotBlueprint";

const FIELD_GROUP_RULES = [
  {
    title: "Identificação",
    matches: [
      "parcel",
      "account",
      "uuid",
      "geoid",
      "placekey",
      "precisely",
      "refresh",
      "updated",
    ],
  },
  {
    title: "Localização e lote",
    matches: [
      "address",
      "sadd",
      "city",
      "county",
      "state",
      "zip",
      "lat",
      "lon",
      "acre",
      "sqft",
      "subdivision",
      "neighborhood",
    ],
  },
  {
    title: "Proprietário e correspondência",
    matches: ["owner", "deed", "mail", "dpv", "cass", "rdi", "usps"],
  },
  {
    title: "Valores e impostos",
    matches: ["val", "sale", "tax", "price", "affordability"],
  },
  {
    title: "Construções",
    matches: [
      "struct",
      "yearbuilt",
      "numunit",
      "numstor",
      "numroom",
      "bed",
      "bath",
      "building",
      "bldg",
    ],
  },
  {
    title: "Uso do solo e zoning",
    matches: ["legal", "zoning", "use", "lbcs", "qoz"],
  },
  {
    title: "Risco e contexto",
    matches: [
      "fema",
      "flood",
      "risk",
      "elevation",
      "roughness",
      "transmission",
      "wetland",
    ],
  },
  {
    title: "Demografia e mercado",
    matches: ["census", "population", "household", "housing", "income"],
  },
];

const AI_SYSTEM_PROMPT = `You are a balanced but cautious US tax-sale property screener.
Use the supplied TOON and Google Search. Answer briefly in Portuguese.
Treat request_context.current_date_utc as today's date. Compare every listing and sale date against it before describing something as past, recent, future or old.
Determine only: the property's local context, whether a vacant lot is realistically buildable, and nearby market signals.
Describe context through development pattern, density, access and any supportable safety signal; do not infer safety from appearance alone.
Use rural_residential_subdivision only for platted residential lots outside a dense urban area; it does not mean farm, ranch or agricultural acreage.
For buildability, prefer official zoning/code sources and verify jurisdiction, zoning, permitted use, minimum lot area, frontage and setbacks.
Never assume grandfathering, variance, easement, lot combination or special permission. Unverified facts must be UNKNOWN.
For houses, use NOT_APPLICABLE for vacant-lot buildability and briefly flag relevant record or location concerns.
For market evidence, search public real-estate listing pages and official sale records when available. For vacant land, also search specialized land portals such as Land.com, LandSearch, LandWatch and Land And Farm when indexed. Prefer the same street, then nearby properties with the same type and similar land or building area. Separate active asking prices from recorded or reported sales. Treat land portal prices as seller-side positioning, not proof of value or completed demand. Never invent a comparable, price, date or distance.
Treat verified nearby sales as evidence of marketability even when the property has serious constraints. Explain plausible exit strategies without treating a comparable sale price as the subject property's value.
Use DISCARD only for a verified hard blocker or when constraints leave no credible use or exit. Otherwise prefer RESEARCH and state what could make the deal workable.
Return only valid JSON matching the requested structure.`;

const AI_INITIAL_REQUEST = `Return a concise screening result. Include at most 5 comparables:
{
  "verdict": "KEEP | RESEARCH | DISCARD",
  "property_type": "vacant_land | house | other | unknown",
  "context": {
    "classification": "urban | suburban | exurban | rural | rural_residential_subdivision | transition | unknown",
    "summary": "maximum 3 short sentences"
  },
  "buildability": {
    "status": "LIKELY_BUILDABLE | UNCERTAIN | LIKELY_NOT_BUILDABLE | NOT_APPLICABLE",
    "summary": "maximum 3 short sentences",
    "verified_rules": ["short rule with source"],
    "blockers": ["short item"],
    "unknowns": ["short item"]
  },
  "market": {
    "summary": "maximum 2 short sentences",
    "land_listing_positioning": "maximum 2 short sentences about asking-price position, or unknown",
    "possible_exits": ["maximum 3 realistic strategies"],
    "comparables": [
      {
        "status": "FOR_SALE | SOLD",
        "address": "string",
        "price": "string",
        "lot_or_home_size": "string",
        "unit_price": "price per acre or sqft when calculable",
        "date": "string",
        "source": "string"
      }
    ]
  },
  "critical_flags": ["maximum 5 short items"],
  "next_checks": ["maximum 5 short items"]
}`;

const AI_FOLLOW_UP_PROMPT = `Answer follow-up questions briefly in Portuguese.
Keep using the supplied property context and conversation history.
Use Google Search when the question needs current zoning, regulations, listings or sales.
When an image is attached, analyze only visible evidence relevant to the question. Do not infer ownership, boundaries, permits, zoning, structural integrity or legal access from appearance alone.
Clearly separate verified facts, reasonable inferences and unknowns.`;

const AI_FOLLOW_UP_CONTEXT_REQUEST =
  "Use this property data as background for the conversation. Answer the latest user question directly.";

const MAX_AI_IMAGE_DIMENSION = 1024;
const MAX_AI_IMAGE_BYTES = 1_500_000;

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Não foi possível ler a imagem"));
    reader.readAsDataURL(blob);
  });
}

async function prepareImageForAi(file) {
  if (!file?.type?.startsWith("image/")) {
    throw new Error("Cole ou selecione um arquivo de imagem");
  }

  const image = await createImageBitmap(file);
  const scale = Math.min(
    1,
    MAX_AI_IMAGE_DIMENSION / Math.max(image.width, image.height),
  );
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);
  image.close();

  const renderBlob = (quality) =>
    new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) =>
          blob
            ? resolve(blob)
            : reject(new Error("Não foi possível processar a imagem")),
        "image/jpeg",
        quality,
      );
    });

  let compressed = await renderBlob(0.82);
  if (compressed.size > MAX_AI_IMAGE_BYTES) {
    compressed = await renderBlob(0.65);
  }
  if (compressed.size > MAX_AI_IMAGE_BYTES) {
    throw new Error("A imagem continua muito grande após a compressão");
  }

  const dataUrl = await blobToDataUrl(compressed);
  return {
    data: dataUrl.split(",")[1],
    height,
    mimeType: "image/jpeg",
    previewUrl: dataUrl,
    size: compressed.size,
    width,
  };
}

function hasValue(value) {
  return value !== null && value !== undefined && value !== "";
}

function readableLabel(value) {
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatStructuredAddress(value, fallbackFields = {}) {
  if (!value) return "";

  let address = value;
  if (typeof value === "string" && value.trim().startsWith("{")) {
    try {
      address = JSON.parse(value);
    } catch {
      return value;
    }
  }
  if (typeof address !== "object") return String(address);

  const street =
    address.address ||
    [address.saddno, address.saddstr].filter(Boolean).join(" ") ||
    fallbackFields.address;
  const city = address.scity || address.city || fallbackFields.city;
  const state =
    address.state ||
    address.state2 ||
    address.sstate ||
    fallbackFields.state2 ||
    fallbackFields.state_code;
  const zip = address.szip || address.zip || fallbackFields.szip;
  const stateZip = [state, zip].filter(Boolean).join(" ");
  const locality = [city, stateZip].filter(Boolean).join(", ");

  return [street, locality].filter(Boolean).join(", ");
}

function displayValue(value, key, fields = {}) {
  if (!hasValue(value)) return "—";
  if (key?.includes("address") || key === "mailadd") {
    return formatStructuredAddress(value, fields);
  }
  if (Array.isArray(value)) {
    return value.every((item) => typeof item !== "object")
      ? value.join(", ")
      : `${value.length} item(s)`;
  }
  if (typeof value === "object") return formatStructuredAddress(value, fields);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

function extractFeatures(data) {
  const collection = data?.parcels || data;
  return Array.isArray(collection?.features) ? collection.features : [];
}

function getFields(feature) {
  return feature?.properties?.fields || {};
}

function buildProperty(feature) {
  const fields = getFields(feature);
  return {
    parcel_number: fields.parcelnumb,
    owner_name: fields.owner,
    property_address: formatStructuredAddress(
      fields.address || fields.original_address,
      fields,
    ),
    city: fields.city,
    county: fields.county,
    state_code: fields.state_code || fields.state2,
    zip_code: fields.szip || fields.szip5,
    latitude: fields.lat,
    longitude: fields.lon,
    legal_description: fields.legaldesc,
    zoning_code: fields.zoning,
    zoning_description: fields.zoning_description,
    alt_key: fields.alt_key || fields.altkey,
    lot_area: fields.ll_gisacre,
    appraisal_value: fields.parval,
    land_value: fields.landval,
    improvement_value: fields.improvval,
    fema_zone_code: fields.fema_flood_zone,
    regrid_ll_uuid: feature?.properties?.ll_uuid,
    regrid_path: feature?.properties?.path,
  };
}

function buildUrlFromRules(rawRules, property) {
  if (!rawRules) return null;

  let rules = rawRules;
  if (typeof rawRules === "string") {
    try {
      rules = JSON.parse(rawRules);
    } catch {
      return null;
    }
  }

  if (!rules?.url_template) return null;

  const baseValues = {
    parcel_number: property?.parcel_number,
    alt_key: property?.alt_key,
    property_address: property?.property_address,
    owner_name: property?.owner_name,
    latitude: property?.latitude,
    longitude: property?.longitude,
  };
  const computed = { ...baseValues };

  Object.entries(rules.transforms || {}).forEach(([targetToken, config]) => {
    const sourceKey = config?.source || targetToken;
    let value = String(baseValues[sourceKey] ?? property?.[sourceKey] ?? "").trim();
    if (config?.stripChars) {
      const escaped = String(config.stripChars).replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&",
      );
      value = value.replace(new RegExp(`[${escaped}]`, "g"), "");
    }
    if (config?.stripNonAlnum) value = value.replace(/[^a-zA-Z0-9]/g, "");
    if (config?.upper) value = value.toUpperCase();
    if (config?.lower) value = value.toLowerCase();
    if (config?.prefix) value = `${config.prefix}${value}`;
    if (config?.suffix) value = `${value}${config.suffix}`;
    computed[targetToken] = value;
  });

  const url = String(rules.url_template).replace(
    /{{\s*([a-zA-Z0-9_]+)\s*}}/g,
    (_, token) =>
      encodeURIComponent(
        String(computed[token] ?? property?.[token] ?? "").trim(),
      ),
  );

  return /^https?:\/\//i.test(url) ? url : null;
}

function buildGoogleMapsUrl(property) {
  const query =
    hasValue(property.latitude) && hasValue(property.longitude)
      ? `${property.latitude},${property.longitude}`
      : property.property_address;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query || "")}`;
}

function buildFemaUrl(property) {
  const query =
    hasValue(property.latitude) && hasValue(property.longitude)
      ? `${property.longitude},${property.latitude}`
      : property.property_address;
  return query
    ? `https://msc.fema.gov/portal/search?AddressQuery=${encodeURIComponent(query)}`
    : "https://msc.fema.gov/portal/home";
}

function buildZillowUrl(property) {
  const latitude = Number(property?.latitude);
  const longitude = Number(property?.longitude);

  if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
    const delta = 0.002;
    const searchQueryState = {
      pagination: {},
      isMapVisible: true,
      mapBounds: {
        west: longitude - delta,
        east: longitude + delta,
        south: latitude - delta,
        north: latitude + delta,
      },
      filterState: {
        sort: { value: "globalrelevanceex" },
        rs: { value: true },
        fsba: { value: false },
        fsbo: { value: false },
        nc: { value: false },
        cmsn: { value: false },
        auc: { value: false },
        fore: { value: false },
      },
      isListVisible: true,
      mapZoom: 17,
    };
    return `https://www.zillow.com/homes/recently_sold/?searchQueryState=${encodeURIComponent(JSON.stringify(searchQueryState))}`;
  }

  const query =
    property?.property_address ||
    [property?.city, property?.state_code, property?.zip_code]
      .filter(Boolean)
      .join(", ");
  return query
    ? `https://www.zillow.com/homes/${encodeURIComponent(query)}/`
    : "https://www.zillow.com/";
}

function openExternal(url) {
  window.open(url, "_blank", "noopener,noreferrer");
}

function DataSection({ title, fields, keys }) {
  const entries = keys
    .filter((key) => hasValue(fields[key]))
    .map((key) => [key, fields[key]]);

  if (!entries.length) return null;

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
        {title}
      </h3>
      <div className="mt-3 grid gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
        {entries.map(([key, value]) => (
          <div key={key} className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              {readableLabel(key)}
            </div>
            <div className="mt-1 break-words text-sm font-medium text-gray-800 dark:text-gray-200">
              {displayValue(value, key, fields)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function categorizeFields(fields) {
  const groups = FIELD_GROUP_RULES.map((group) => ({
    title: group.title,
    keys: [],
  }));
  const otherKeys = [];

  Object.entries(fields)
    .filter(([, value]) => hasValue(value))
    .sort(([left], [right]) => left.localeCompare(right))
    .forEach(([key]) => {
      const normalizedKey = key.toLowerCase();
      const prioritizedGroup = [
        { index: 2, matches: ["mail", "owner", "deed", "dpv", "cass", "rdi", "usps"] },
        {
          index: 4,
          matches: [
            "building",
            "bldg",
            "struct",
            "yearbuilt",
            "numunit",
            "numstor",
            "numroom",
            "bed",
            "bath",
          ],
        },
        {
          index: 6,
          matches: ["fema", "flood", "risk", "elevation", "roughness", "transmission"],
        },
        { index: 5, matches: ["zoning", "legal", "lbcs", "qoz", "usecode", "usedesc"] },
      ].find((group) =>
        group.matches.some((match) => normalizedKey.includes(match)),
      );
      const groupIndex =
        prioritizedGroup?.index ??
        FIELD_GROUP_RULES.findIndex((group) =>
          group.matches.some((match) => normalizedKey.includes(match)),
        );
      if (groupIndex >= 0) groups[groupIndex].keys.push(key);
      else otherKeys.push(key);
    });

  if (otherKeys.length) {
    groups.push({ title: "Outros dados do Regrid", keys: otherKeys });
  }

  return groups.filter((group) => group.keys.length);
}

function CollectionCards({ title, value }) {
  const items = Array.isArray(value?.features)
    ? value.features
    : Array.isArray(value)
      ? value
      : [];
  const records = items
    .map((item) => item?.properties?.fields || item?.properties || item)
    .filter((item) => item && typeof item === "object");

  if (!records.length) return null;

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
        {title}
      </h3>
      <div className="grid gap-3 lg:grid-cols-2">
        {records.map((record, index) => (
          <DataSection
            key={`${title}-${index}`}
            title={`${title} ${index + 1}`}
            fields={record}
            keys={Object.keys(record)}
          />
        ))}
      </div>
    </section>
  );
}

function AnalysisList({ title, items, tone = "gray" }) {
  if (!Array.isArray(items) || !items.length) return null;
  const toneClasses = {
    gray: "border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/50",
    red: "border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/20",
    green:
      "border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/20",
    amber:
      "border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/20",
  };

  return (
    <section className={`rounded-xl border p-4 ${toneClasses[tone]}`}>
      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
        {title}
      </h4>
      <ul className="mt-2 list-disc space-y-1.5 pl-5 text-sm text-gray-700 dark:text-gray-300">
        {items.map((item, index) => (
          <li key={`${title}-${index}`}>{displayValue(item)}</li>
        ))}
      </ul>
    </section>
  );
}

function extractGroundingSources(result) {
  const chunks =
    result?.candidates?.[0]?.groundingMetadata?.groundingChunks || [];
  const seen = new Set();

  return chunks
    .map((chunk) => chunk?.web)
    .filter((source) => source?.uri && !seen.has(source.uri) && seen.add(source.uri))
    .map((source) => ({ title: source.title || source.uri, url: source.uri }));
}

function AiContextAnalysis({ result }) {
  const analysis = result?.parsedJson;
  const sources = extractGroundingSources(result);

  if (!analysis) {
    const text =
      result?.candidates?.[0]?.content?.parts
        ?.map((part) => part.text)
        .filter(Boolean)
        .join("") || "A IA não retornou uma análise estruturada.";
    return (
      <Alert color="warning">
        <pre className="whitespace-pre-wrap text-xs">{text}</pre>
      </Alert>
    );
  }

  const context = analysis.property_context || {};
  const buildability = analysis.buildability || {};
  const profile = analysis.property_profile || {};
  const auctionFilter = analysis.auction_filter || {};
  const house = analysis.house_analysis || {};

  return (
    <div className="space-y-4">
      <section className="rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/20">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">
              Contexto da propriedade
            </div>
            <h3 className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
              {context.classification || "Contexto não determinado"}
            </h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {context.development_stage && (
              <Badge color="info">{context.development_stage}</Badge>
            )}
            {profile.type && <Badge color="gray">{profile.type}</Badge>}
          </div>
        </div>
        <p className="mt-3 text-sm leading-6 text-gray-700 dark:text-gray-300">
          {context.explanation || "—"}
        </p>
        {context.density_pattern && (
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <span className="font-semibold">Padrão:</span>{" "}
            {context.density_pattern}
          </p>
        )}
        {context.safety_assessment && (
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <span className="font-semibold">Segurança:</span>{" "}
            {context.safety_assessment}
          </p>
        )}
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Buildability
            </div>
            <div className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
              {buildability.status || "UNCERTAIN"}
            </div>
          </div>
          <Badge
            color={
              buildability.status === "LIKELY_BUILDABLE"
                ? "success"
                : buildability.status === "LIKELY_NOT_BUILDABLE"
                  ? "failure"
                  : "warning"
            }
          >
            Confiança {buildability.confidence || "LOW"}
          </Badge>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-400">
              Jurisdição
            </div>
            <div className="mt-1 text-sm font-medium">
              {buildability.jurisdiction || "Não confirmada"}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-400">
              Zoning
            </div>
            <div className="mt-1 text-sm font-medium">
              {buildability.zoning || "Não confirmado"}
            </div>
          </div>
        </div>
        {buildability.lot_shape_assessment && (
          <p className="mt-4 text-sm leading-6 text-gray-700 dark:text-gray-300">
            <span className="font-semibold">Formato do lote:</span>{" "}
            {buildability.lot_shape_assessment}
          </p>
        )}
        {buildability.dimensional_analysis && (
          <p className="mt-2 text-sm leading-6 text-gray-700 dark:text-gray-300">
            <span className="font-semibold">Dimensões:</span>{" "}
            {buildability.dimensional_analysis}
          </p>
        )}
      </section>

      {Array.isArray(buildability.verified_rules) &&
        buildability.verified_rules.length > 0 && (
          <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
              Regras verificadas
            </h4>
            <div className="mt-3 space-y-3">
              {buildability.verified_rules.map((rule, index) => (
                <div
                  key={`rule-${index}`}
                  className="rounded-lg border border-gray-100 p-3 dark:border-gray-800"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-sm font-semibold">{rule.rule}</div>
                    <Badge
                      color={
                        rule.result === "PASS"
                          ? "success"
                          : rule.result === "FAIL"
                            ? "failure"
                            : "warning"
                      }
                    >
                      {rule.result || "UNKNOWN"}
                    </Badge>
                  </div>
                  <div className="mt-2 grid gap-2 text-xs text-gray-600 dark:text-gray-400 md:grid-cols-2">
                    <div>Exigência: {rule.requirement || "—"}</div>
                    <div>Propriedade: {rule.property_measurement || "—"}</div>
                  </div>
                  {rule.source && (
                    <div className="mt-2 text-xs text-blue-700">
                      Fonte: {rule.source}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

      <div className="grid gap-4 lg:grid-cols-2">
        <AnalysisList
          title="Red flags"
          items={analysis.red_flags}
          tone="red"
        />
        <AnalysisList
          title="Possibilidades de trabalho"
          items={analysis.workable_angles}
          tone="green"
        />
        <AnalysisList
          title="Bloqueios de buildability"
          items={buildability.blockers}
          tone="red"
        />
        <AnalysisList
          title="Pontos ainda desconhecidos"
          items={buildability.unknowns}
          tone="amber"
        />
        <AnalysisList
          title="Sinais sobre a casa"
          items={house.condition_signals}
        />
        <AnalysisList
          title="Próximas verificações"
          items={analysis.next_checks}
          tone="amber"
        />
      </div>

      <section className="rounded-xl border-2 border-gray-900 bg-gray-950 p-4 text-white">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-400">
              Filtro de leilão
            </div>
            <div className="mt-1 text-xl font-semibold">
              {auctionFilter.recommendation || "RESEARCH"} · Rating{" "}
              {auctionFilter.rating || "—"}
            </div>
          </div>
        </div>
        <p className="mt-3 text-sm leading-6 text-gray-300">
          {auctionFilter.reason || "—"}
        </p>
      </section>

      {sources.length > 0 && (
        <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
          <h4 className="text-sm font-semibold">Fontes pesquisadas</h4>
          <div className="mt-3 flex flex-wrap gap-2">
            {sources.map((source) => (
              <a
                key={source.url}
                href={source.url}
                target="_blank"
                rel="noreferrer"
                className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-blue-700 hover:border-blue-400 dark:border-gray-700"
              >
                {source.title}
              </a>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default function QuickSearchPage() {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [regridData, setRegridData] = useState(null);
  const [selectedRegridData, setSelectedRegridData] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [resources, setResources] = useState({ county: null, links: [] });
  const [aiResult, setAiResult] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [followUpInput, setFollowUpInput] = useState("");
  const [followUpImage, setFollowUpImage] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [loadingAction, setLoadingAction] = useState(null);

  const features = useMemo(() => extractFeatures(regridData), [regridData]);
  const selectedFeature = useMemo(
    () => extractFeatures(selectedRegridData)[0] || null,
    [selectedRegridData],
  );
  const property = useMemo(
    () => (selectedFeature ? buildProperty(selectedFeature) : null),
    [selectedFeature],
  );
  const fields = useMemo(() => getFields(selectedFeature), [selectedFeature]);
  const groupedFields = useMemo(() => categorizeFields(fields), [fields]);
  const regridMetadata = useMemo(() => {
    const properties = selectedFeature?.properties || {};
    return Object.fromEntries(
      Object.entries(properties).filter(
        ([key, value]) =>
          key !== "fields" &&
          key !== "addresses" &&
          key !== "enhanced_ownership" &&
          hasValue(value) &&
          typeof value !== "object",
      ),
    );
  }, [selectedFeature]);

  const selectedBuildings = useMemo(() => {
    const allBuildings = selectedRegridData?.buildings?.features || [];
    const uuid = selectedFeature?.properties?.ll_uuid;
    if (!uuid) return allBuildings;
    return allBuildings.filter((building) => {
      const uuids = building?.properties?.ll_uuids || [];
      return uuids.includes(uuid) || building?.properties?.ll_uuid === uuid;
    });
  }, [selectedRegridData, selectedFeature]);

  const parcelMetrics = useMemo(
    () => measureParcelGeometry(selectedFeature?.geometry),
    [selectedFeature],
  );
  const appraiserUrl = useMemo(
    () =>
      property
        ? buildUrlFromRules(
            resources.county?.appraiser_rules_json,
            property,
          )
        : null,
    [property, resources.county?.appraiser_rules_json],
  );
  const externalResources = useMemo(
    () =>
      resources.links.map((link) =>
        link.link_type === "PROPERTY_APPRAISER" && appraiserUrl
          ? { ...link, url: appraiserUrl }
          : link,
      ),
    [appraiserUrl, resources.links],
  );
  const aiContext = useMemo(
    () => ({
      request_context: {
        current_date_utc: new Date().toISOString().slice(0, 10),
        requested_at_utc: new Date().toISOString(),
        requester_timezone:
          Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
      },
      property_summary: property,
      regrid_fields: fields,
      parcel_geometry: {
        geometry_type: selectedFeature?.geometry?.type,
        calculated_area_acres: parcelMetrics.areaAcres,
        calculated_area_sqft: parcelMetrics.areaSqft,
        calculated_perimeter_ft: parcelMetrics.perimeterFeet,
        face_lengths_ft: parcelMetrics.edges.map((edge, index) => ({
          face: index + 1,
          length_ft: edge.lengthFeet,
        })),
      },
      regrid_metadata: regridMetadata,
      addresses: selectedFeature?.properties?.addresses,
      enhanced_ownership: selectedFeature?.properties?.enhanced_ownership,
      buildings: selectedBuildings.map(
        (building) => building?.properties || building,
      ),
      zoning: (selectedRegridData?.zoning?.features || []).map(
        (zoningFeature) => zoningFeature?.properties || zoningFeature,
      ),
    }),
    [
      fields,
      parcelMetrics,
      property,
      regridMetadata,
      selectedBuildings,
      selectedFeature,
      selectedRegridData,
    ],
  );

  async function runSearch(event) {
    event.preventDefault();
    if (!query.trim()) return;

    setSearching(true);
    setSearchError(null);
    setSelectedIndex(null);
    setRegridData(null);
    setSelectedRegridData(null);
    clearResearchResults();

    try {
      const data = await quickSearchApi.searchRegrid(query.trim());
      setRegridData(data);
    } catch (error) {
      setSearchError(error?.message || "Falha ao consultar o Regrid");
    } finally {
      setSearching(false);
    }
  }

  function clearResearchResults() {
    setResources({ county: null, links: [] });
    setAiResult(null);
    setChatMessages([]);
    setFollowUpInput("");
    setFollowUpImage(null);
    setActionError(null);
  }

  async function selectProperty(index) {
    const candidate = features[index];
    const llUuid = candidate?.properties?.ll_uuid;
    if (!llUuid) {
      setActionError("O Regrid não retornou ll_uuid para esta propriedade");
      return;
    }

    setSelectedIndex(index);
    setSelectedRegridData(null);
    clearResearchResults();
    setLoadingAction("regrid-details");

    try {
      const details = await quickSearchApi.getRegridParcelDetails(llUuid);
      const detailedFeature = extractFeatures(details)[0] || candidate;
      const selectedProperty = buildProperty(detailedFeature);
      setSelectedRegridData(
        extractFeatures(details).length
          ? details
          : { parcels: { type: "FeatureCollection", features: [candidate] } },
      );

      if (selectedProperty.state_code && selectedProperty.county) {
        try {
          const data = await quickSearchApi.getCountyResources({
            stateCode: selectedProperty.state_code,
            county: selectedProperty.county,
          });
          setResources(data);
        } catch {
          setResources({ county: null, links: [] });
        }
      }
    } catch (error) {
      setSelectedIndex(null);
      setActionError(
        error?.message || "Não foi possível carregar os detalhes do Regrid",
      );
    } finally {
      setLoadingAction(null);
    }
  }

  async function runAction(name, action) {
    setLoadingAction(name);
    setActionError(null);
    try {
      await action();
    } catch (error) {
      setActionError(error?.message || "Não foi possível concluir a consulta");
    } finally {
      setLoadingAction(null);
    }
  }

  function queryAi() {
    return runAction("ai", async () => {
      const result = await aiApi.evaluate({
        systemPrompt: AI_SYSTEM_PROMPT,
        userPrompt: AI_INITIAL_REQUEST,
        state: aiContext,
        enableGoogleSearch: true,
        inputFormat: "toon",
        omitEmptyValues: true,
      });
      setAiResult(result);
      setChatMessages([]);
      setFollowUpInput("");
      setFollowUpImage(null);
    });
  }

  async function attachFollowUpImage(file) {
    if (!file) return;
    setActionError(null);
    setLoadingAction("ai-image");
    try {
      setFollowUpImage(await prepareImageForAi(file));
    } catch (error) {
      setActionError(error?.message || "Não foi possível preparar a imagem");
    } finally {
      setLoadingAction(null);
    }
  }

  function handleFollowUpPaste(event) {
    const imageItem = Array.from(event.clipboardData?.items || []).find((item) =>
      item.type?.startsWith("image/"),
    );
    if (!imageItem) return;
    event.preventDefault();
    attachFollowUpImage(imageItem.getAsFile());
  }

  function queryFollowUp(event) {
    event.preventDefault();
    const attachedImage = followUpImage;
    const question =
      followUpInput.trim() ||
      (attachedImage
        ? "Analise esta imagem no contexto da propriedade e destaque somente o que for relevante."
        : "");
    if (!question || !aiResult) return;

    return runAction("ai-follow-up", async () => {
      const result = await aiApi.evaluate({
        systemPrompt: AI_FOLLOW_UP_PROMPT,
        userPrompt: AI_FOLLOW_UP_CONTEXT_REQUEST,
        state: aiContext,
        conversation: [
          { role: "model", text: extractModelText(aiResult) },
          ...chatMessages.map(({ role, text }) => ({ role, text })),
          {
            role: "user",
            text: question,
            images: attachedImage
              ? [
                  {
                    data: attachedImage.data,
                    mimeType: attachedImage.mimeType,
                  },
                ]
              : [],
          },
        ],
        enableGoogleSearch: true,
        inputFormat: "toon",
        omitEmptyValues: true,
      });
      const answer = extractModelText(result);
      setChatMessages((current) => [
        ...current,
        {
          role: "user",
          text: question,
          imagePreview: attachedImage?.previewUrl,
        },
        {
          role: "model",
          text: answer || "A IA não retornou uma resposta.",
          sources: extractQuickSearchSources(result),
        },
      ]);
      setFollowUpInput("");
      setFollowUpImage(null);
    });
  }

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      <h1 className="text-3xl font-semibold tracking-tight text-gray-900 dark:text-white">
        Quick Search
      </h1>

      <form
        onSubmit={runSearch}
        className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900"
      >
        <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Parcel / APN
        </label>
        <div className="mt-2 flex flex-col gap-3 md:flex-row">
          <div className="relative flex-1">
            <LuSearch className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Digite o parcel number"
              className="h-12 w-full rounded-xl border border-gray-300 bg-white pl-12 pr-4 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
            />
          </div>
          <Button
            type="submit"
            color="blue"
            disabled={searching || !query.trim()}
            className="h-12 px-7"
          >
            {searching ? <Spinner size="sm" className="mr-2" /> : null}
            Pesquisar no Regrid
          </Button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          A busca é nacional. Se o APN existir em mais de um local, escolha o
          estado e o condado corretos no resultado.
        </p>
      </form>

      {searchError && <Alert color="failure">{searchError}</Alert>}
      {actionError && <Alert color="failure">{actionError}</Alert>}

      {regridData && features.length === 0 && (
        <Alert color="warning">Nenhuma propriedade encontrada no Regrid.</Alert>
      )}

      {features.length > 0 && !selectedFeature && (
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Escolha a propriedade correta
              </h2>
              <p className="text-sm text-gray-500">
                {features.length} resultado(s) para o APN “{query}”
              </p>
            </div>
            <Badge color={features.length > 1 ? "warning" : "success"}>
              {features.length > 1 ? "APN ambíguo" : "1 resultado"}
            </Badge>
          </div>

          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            {features.map((feature, index) => {
              const resultFields = getFields(feature);
              const state = resultFields.state_code || resultFields.state2;
              return (
                <button
                  key={feature?.properties?.ll_uuid || index}
                  type="button"
                  onClick={() => selectProperty(index)}
                  disabled={loadingAction === "regrid-details"}
                  className="rounded-xl border border-gray-200 p-4 text-left transition hover:border-blue-400 hover:bg-blue-50 disabled:cursor-wait disabled:opacity-60 dark:border-gray-700 dark:hover:bg-blue-950/20"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-700 text-xs font-bold text-white">
                      {state || "US"}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {formatStructuredAddress(
                          resultFields.address || resultFields.original_address,
                          resultFields,
                        ) || "Sem endereço"}
                      </div>
                      <div className="mt-1 text-xs text-gray-500">
                        {[resultFields.county, state, resultFields.szip]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                      <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                        Owner: {resultFields.owner || "—"}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {resultFields.usedesc && (
                          <Badge color="gray">{resultFields.usedesc}</Badge>
                        )}
                        {resultFields.ll_gisacre && (
                          <Badge color="gray">
                            {resultFields.ll_gisacre} ac
                          </Badge>
                        )}
                        <Badge color="success">Selecionar</Badge>
                        {loadingAction === "regrid-details" &&
                          selectedIndex === index && <Spinner size="sm" />}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {selectedFeature && property && (
        <>
          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
              <div>
                <button
                  type="button"
                  onClick={() => setSelectedIndex(null)}
                  className="mb-2 text-xs font-semibold text-blue-700 hover:underline"
                >
                  ← Trocar propriedade
                </button>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {property.property_address || "Sem endereço"}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Parcel {property.parcel_number || "—"} ·{" "}
                  {[property.county, property.state_code]
                    .filter(Boolean)
                    .join(", ")}
                </p>
                <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                  {property.owner_name || "Owner não informado"}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  size="xs"
                  color="dark"
                  onClick={() => openExternal(buildGoogleMapsUrl(property))}
                >
                  <LuMapPin className="mr-1.5" /> Google Maps
                </Button>
                <Button
                  size="xs"
                  color="dark"
                  onClick={() => openExternal(buildFemaUrl(property))}
                >
                  <LuShieldCheck className="mr-1.5" /> FEMA
                </Button>
                <Button
                  size="xs"
                  color="dark"
                  onClick={() => openExternal(buildZillowUrl(property))}
                >
                  <LuBuilding2 className="mr-1.5" /> Zillow
                </Button>
                {property.regrid_path && (
                  <Button
                    size="xs"
                    color="dark"
                    onClick={() =>
                      openExternal(
                        `https://app.regrid.com${property.regrid_path}`,
                      )
                    }
                  >
                    <LuMap className="mr-1.5" /> Regrid
                  </Button>
                )}
                {externalResources.map((link) => (
                  <Button
                    key={link.id}
                    size="xs"
                    color="light"
                    onClick={() => openExternal(link.url)}
                  >
                    <LuExternalLink className="mr-1.5" />
                    {link.label || readableLabel(link.link_type)}
                  </Button>
                ))}
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200 bg-gray-50 p-4 shadow-sm dark:border-gray-800 dark:bg-gray-950/30">
            <div className="space-y-4">
              <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
                <div className="space-y-4">
                  {groupedFields.map((group) => (
                    <DataSection
                      key={group.title}
                      title={group.title}
                      fields={fields}
                      keys={group.keys}
                    />
                  ))}
                </div>
                <div className="space-y-4">
                  <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
                    <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
                      Geometria do lote
                    </h3>
                    <BlueprintPreview
                      parcelGeometry={selectedFeature.geometry}
                      buildingGeometries={selectedBuildings
                        .map((building) => building.geometry)
                        .filter(Boolean)}
                    />
                  </section>
                  <DataSection
                    title="Metadados do Regrid"
                    fields={regridMetadata}
                    keys={Object.keys(regridMetadata)}
                  />
                </div>
              </div>
              <CollectionCards
                title="Endereços"
                value={selectedFeature.properties?.addresses}
              />
              <CollectionCards
                title="Enhanced ownership"
                value={selectedFeature.properties?.enhanced_ownership}
              />
              <CollectionCards
                title="Construções retornadas"
                value={{
                  type: "FeatureCollection",
                  features: selectedBuildings,
                }}
              />
              <CollectionCards
                title="Zoning retornado"
                value={selectedRegridData.zoning}
              />
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                  Property Context & Buildability
                </div>
                <h2 className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">
                  Análise crítica com pesquisa local
                </h2>
                <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-500">
                  Filtra contexto, buildability e sinais de mercado usando apenas
                  dados disponíveis. Preços anunciados e vendas são identificados
                  separadamente.
                </p>
              </div>
              <Button
                color="blue"
                disabled={loadingAction?.startsWith("ai")}
                onClick={queryAi}
              >
                {loadingAction === "ai" ? (
                  <Spinner size="sm" className="mr-2" />
                ) : (
                  <LuBot className="mr-2" />
                )}
                Analisar contexto
              </Button>
            </div>

            {aiResult ? (
              <div className="mt-5 space-y-3">
                <div className="text-xs text-gray-500">
                  Modelo: {aiResult.modelVersion || "Gemini"} · entrada{" "}
                  {(aiResult.inputFormat || "toon").toUpperCase()} · tokens{" "}
                  {aiResult.usageMetadata?.totalTokenCount || "—"}
                </div>
                {aiResult.parsedJson?.verdict ? (
                  <QuickSearchAiAnalysis result={aiResult} />
                ) : (
                  <AiContextAnalysis result={aiResult} />
                )}

                <div className="space-y-3 border-t border-gray-200 pt-4 dark:border-gray-800">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      Perguntas sobre esta análise
                    </h3>
                    <p className="mt-1 text-xs text-gray-500">
                      O contexto e o histórico são mantidos enquanto esta pesquisa
                      permanecer aberta.
                    </p>
                  </div>

                  {chatMessages.length > 0 && (
                    <div className="max-h-[420px] space-y-3 overflow-y-auto rounded-xl bg-gray-50 p-3 dark:bg-gray-950/40">
                      {chatMessages.map((message, index) => (
                        <div
                          key={`${message.role}-${index}`}
                          className={
                            message.role === "user"
                              ? "ml-auto max-w-[85%] rounded-xl bg-blue-600 px-3 py-2 text-sm text-white"
                              : "mr-auto max-w-[92%] rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300"
                          }
                        >
                          {message.imagePreview && (
                            <img
                              src={message.imagePreview}
                              alt="Imagem anexada à pergunta"
                              className="mb-2 max-h-52 w-auto max-w-full rounded-lg object-contain"
                            />
                          )}
                          {message.role === "model" ? (
                            <QuickSearchMarkdown>
                              {message.text}
                            </QuickSearchMarkdown>
                          ) : (
                            <div className="whitespace-pre-wrap leading-6">
                              {message.text}
                            </div>
                          )}
                          {message.role === "model" &&
                            message.sources?.length > 0 && (
                              <div className="mt-2 border-t border-gray-100 pt-2 dark:border-gray-800">
                                <SourceLinks sources={message.sources} />
                              </div>
                            )}
                        </div>
                      ))}
                    </div>
                  )}

                  {followUpImage && (
                    <div className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 p-2 dark:border-blue-900 dark:bg-blue-950/20">
                      <img
                        src={followUpImage.previewUrl}
                        alt="Preview do anexo"
                        className="h-16 w-20 rounded-lg object-cover"
                      />
                      <div className="min-w-0 flex-1 text-xs text-gray-600 dark:text-gray-300">
                        <div className="font-semibold text-gray-900 dark:text-white">
                          Imagem pronta para análise
                        </div>
                        <div className="mt-1">
                          {followUpImage.width} × {followUpImage.height} px ·{" "}
                          {Math.ceil(followUpImage.size / 1024)} KB
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFollowUpImage(null)}
                        className="rounded-lg p-2 text-gray-500 hover:bg-white hover:text-red-600 dark:hover:bg-gray-900"
                        aria-label="Remover imagem"
                      >
                        <LuX />
                      </button>
                    </div>
                  )}

                  <form
                    onSubmit={queryFollowUp}
                    onPaste={handleFollowUpPaste}
                    className="flex flex-col gap-2 sm:flex-row"
                  >
                    <input
                      value={followUpInput}
                      onChange={(event) => setFollowUpInput(event.target.value)}
                      placeholder="Ex.: qual regra ainda preciso confirmar?"
                      className="h-11 flex-1 rounded-xl border border-gray-300 bg-white px-4 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    />
                    <input
                      id="quick-search-ai-image"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(event) => {
                        attachFollowUpImage(event.target.files?.[0]);
                        event.target.value = "";
                      }}
                    />
                    <label
                      htmlFor="quick-search-ai-image"
                      className="inline-flex h-11 cursor-pointer items-center justify-center rounded-lg border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                      {loadingAction === "ai-image" ? (
                        <Spinner size="sm" className="mr-2" />
                      ) : (
                        <LuImagePlus className="mr-2" />
                      )}
                      Imagem
                    </label>
                    <Button
                      type="submit"
                      color="blue"
                      disabled={
                        (!followUpInput.trim() && !followUpImage) ||
                        loadingAction === "ai-image" ||
                        loadingAction === "ai-follow-up"
                      }
                    >
                      {loadingAction === "ai-follow-up" ? (
                        <Spinner size="sm" className="mr-2" />
                      ) : (
                        <LuSend className="mr-2" />
                      )}
                      Perguntar
                    </Button>
                  </form>
                  <p className="text-xs text-gray-500">
                    Cole uma imagem no campo ou selecione um arquivo. Ela é
                    reduzida e enviada somente nesta pergunta.
                  </p>
                </div>
              </div>
            ) : (
              <Alert color="info" className="mt-5">
                A análise não salva a propriedade nem cria avaliação no banco.
                Regras não encontradas em fontes confiáveis serão marcadas como
                desconhecidas.
              </Alert>
            )}
          </section>
        </>
      )}
    </div>
  );
}
